import React, {Component} from 'react'
import {observer, inject} from 'mobx-react'
import {Editor} from 'draft-js'

import Popover from './Popover'
import Suggestions from './Suggestions'

import * as triggers from './triggers'

import style from './autocomplete.sass'

@inject('store') @observer
class AutocompleteEditor extends Component {
  constructor(props) {
    super(props)
    this.autocompleteState = null
  }

  componentDidMount() {
    this.props.onRef(this)
  }
  componentWillUnmount() {
    this.props.onRef(undefined)
  }

  handleChange = (editorState) => {
    const {onAutocompleteChange} = this.props
    this.props.store.algo.single[this.props.name].change(editorState)
    if (onAutocompleteChange) {
      window.requestAnimationFrame(() => {
        onAutocompleteChange(this.getAutocompleteState())
      })
    }
  }

  hasEntityAtSelection() {
    const {
      editorState
    } = this.props.store.algo.single[this.props.name]

    const selection = editorState.getSelection()
    if (!selection.getHasFocus()) {
      return false
    }

    const contentState = editorState.getCurrentContent()
    const block = contentState.getBlockForKey(selection.getStartKey())
    return !!block.getEntityAt(selection.getStartOffset() - 1)
  }

  getAutocompleteRange(name) {
    const trigger = triggers[`${name}_TRIGGER`]
    const {editorState} = this.props.store.algo.single[this.props.name]
    const selection = editorState.getSelection()
    const content = editorState.getCurrentContent()
    const block = content.getBlockForKey(selection.getStartKey())
    if (block.getLength() === 0 || this.hasEntityAtSelection()) {
      return null
    }

    let text = block.getText()
    text = text.substring(0, selection.getStartOffset())
    const index = text.lastIndexOf(trigger)
    if (index === -1) {
      return null
    }
    text = text.substring(index)
    return {
      text,
      start: index,
      end: selection.getStartOffset(),
      type: triggers[name],
      trigger: trigger,
      array: name.toLowerCase() + 's'
    }
  }

  getAutocompleteState(invalidate = true) {
    if (!invalidate) {
      return this.autocompleteState
    }

    let indicatorRange = this.getAutocompleteRange('INDICATOR')
    let fundamentalRange = this.getAutocompleteRange('FUNDAMENTAL')
    let alternativeRange = this.getAutocompleteRange('ALTERNATIVE')
    if (!indicatorRange && !fundamentalRange && !alternativeRange) {
      this.autocompleteState = null
      return null
    }

    if (!indicatorRange) {
      indicatorRange = {start: -1}
    }
    if (!fundamentalRange) {
      fundamentalRange = {start: -1}
    }
    if (!alternativeRange) {
      alternativeRange = {start: -1}
    }

    let range
    if (indicatorRange.start > fundamentalRange.start && indicatorRange.start > alternativeRange.start) {
      range = indicatorRange
    }
    if (fundamentalRange.start > indicatorRange.start && fundamentalRange.start > alternativeRange.start) {
      range = fundamentalRange
    }
    if (alternativeRange.start > indicatorRange.start && alternativeRange.start > fundamentalRange.start) {
      range = alternativeRange
    }

    if (!range) {
      this.autocompleteState = null
      return null
    }

    this.props.store.signal.setValues({type: range.array})

    const type = range.type
    const trigger = range.trigger

    const triggers = document.querySelectorAll('.trigger')
    const triggerNode = triggers[triggers.length - 1]
    let left, top
    if (triggerNode) {
      const rangeRect = triggerNode.getBoundingClientRect()
      left = rangeRect.left
      top = rangeRect.bottom
      if (this.editor) {
        const wrapperRect = this.wrapper.getBoundingClientRect()
        left -= wrapperRect.left
        top -= wrapperRect.top
      }
    } else if (!this.autocompleteState) {
      this.autocompleteState = null
      return null
    } else {
      left = this.autocompleteState.left
      top = this.autocompleteState.top
    }

    this.autocompleteState = {
      trigger,
      type,
      left,
      top,
      text: range.text,
      selectedIndex: 0
    }
    return this.autocompleteState
  }

  commitSelection = () => {
    const {
      onAutocompleteChange
    } = this.props
    let autocompleteState = this.getAutocompleteState(false)
    if (!autocompleteState) {
      return false
    }
    this.onMentionSelect()
    this.autocompleteState = null

    if (onAutocompleteChange) {
      onAutocompleteChange(null)
    }
    return true
  }

  onMentionSelect() {
    let autocompleteState = this.getAutocompleteState(false)
    const insertState = this.getInsertState(autocompleteState.trigger)
    const {onInsert} = this.props
    this.props.store.signal.setValues({insertState, insertFunc: onInsert})
  }

  getInsertState(trigger) {
    const {
      editorState
    } = this.props.store.algo.single[this.props.name]
    const currentSelectionState = editorState.getSelection()
    const end = currentSelectionState.getAnchorOffset()
    const anchorKey = currentSelectionState.getAnchorKey()
    const currentContent = editorState.getCurrentContent()
    const currentBlock = currentContent.getBlockForKey(anchorKey)
    const blockText = currentBlock.getText()
    const start = blockText.substring(0, end).lastIndexOf(trigger)
    return {
      editorState,
      start,
      end,
      trigger
    }
  }

  focusEditor = () => {
    this.editor.focus()
  }

  handleReturn = () => {
    if (this.props.autocompleteState) {
      return 'handled'
    }
    return 'not-handled'
  }

  render() {
    const {autocompleteState, onAutocompleteChange, handleBeforeInput, handlePastedText, onFocus, onBlur, focus, hasErrors, showModal} = this.props
    const {editorState} = this.props.store.algo.single[this.props.name]
    return(
      <div className={focus ? 'focus' : ''} ref={el => this.wrapper = el}>
        <div
          onClick={this.focusEditor}
          styleName={hasErrors ? ' editor-error' : 'editor'}
          tabIndex={-1}
        >
          <Editor
            editorState={editorState}
            onChange={this.handleChange}
            onAutocompleteChange={onAutocompleteChange}
            handleBeforeInput={handleBeforeInput}
            handlePastedText={handlePastedText}
            handleReturn={this.handleReturn}
            onFocus={onFocus}
            onBlur={onBlur}
            placeholder={'Start by typing your signal or clicking on a blue button below to launch widgets'}
            ref={el => this.editor = el}
          />
          {autocompleteState &&
            <Popover
              popoverState={autocompleteState}
              editorFocused={focus}
              commitSelection={this.commitSelection}
              showModal={showModal}
            />
          }
        </div>
        <Suggestions
          name={this.props.name}
          editorState={editorState}
          handleChange={this.handleChange}
          onFocus={onFocus}
        />
      </div>
    )
  }
}

export default AutocompleteEditor
